import React from "react";
import { Box, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import BedtimeOutlinedIcon from "@mui/icons-material/BedtimeOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import BackHandOutlinedIcon from "@mui/icons-material/BackHandOutlined";
import HearingOutlinedIcon from "@mui/icons-material/HearingOutlined";
import AirOutlinedIcon from "@mui/icons-material/AirOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import MensajeVennDiagram from "./diagrams/MensajeVennDiagram";

const SIN_META = {
  pereza: { palette: "warning", Icon: BedtimeOutlinedIcon },
  soberbia: { palette: "secondary", Icon: EmojiEventsOutlinedIcon },
  envidia: { palette: "error", Icon: TrendingUpOutlinedIcon },
  avaricia: { palette: "success", Icon: SavingsOutlinedIcon },
};

const SENTIDO_META = {
  vista: { Icon: VisibilityOutlinedIcon, palette: "info" },
  tacto: { Icon: BackHandOutlinedIcon, palette: "primary" },
  oido: { Icon: HearingOutlinedIcon, palette: "secondary" },
  olfato: { Icon: AirOutlinedIcon, palette: "warning" },
  gusto: { Icon: RestaurantOutlinedIcon, palette: "success" },
  ser: { Icon: PsychologyOutlinedIcon, palette: "error" },
};

const SectionHeader = ({ title, accent, icon: Icon }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
    {Icon ? (
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(accent, 0.12),
          color: accent,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 15 }} />
      </Box>
    ) : null}
    <Typography
      variant="caption"
      sx={{ fontWeight: 800, letterSpacing: 0.2, color: accent, lineHeight: 1.3 }}
    >
      {title}
    </Typography>
  </Stack>
);

const TintCard = ({ children, color, sx }) => (
  <Box
    sx={{
      p: 1.1,
      borderRadius: 2,
      border: "1px solid",
      borderColor: alpha(color, 0.22),
      bgcolor: alpha(color, 0.05),
      backgroundImage: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, transparent 70%)`,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SinCard = ({ name, example, color, icon: Icon }) => (
  <TintCard color={color} sx={{ height: "100%" }}>
    <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mb: 0.6 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1.5,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(color, 0.14),
          color,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 800, color, lineHeight: 1.3, pt: 0.35 }}>
        {name}
      </Typography>
    </Stack>
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: "block" }}>
      {example}
    </Typography>
  </TintCard>
);

const SentidoCard = ({ label, quote, color, icon: Icon }) => (
  <TintCard color={color}>
    <Stack direction="row" spacing={0.75} alignItems="flex-start">
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 1.25,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(color, 0.12),
          color,
          mt: 0.1,
        }}
      >
        <Icon sx={{ fontSize: 14 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        <Box component="span" sx={{ fontWeight: 800, color: "text.primary" }}>
          {label}
        </Box>{" "}
        <Box component="em" sx={{ fontStyle: "italic" }}>
          {quote}
        </Box>
      </Typography>
    </Stack>
  </TintCard>
);

const StoryStep = ({ text, color }) => (
  <Box
    sx={{
      pl: 1.1,
      borderLeft: `3px solid ${alpha(color, 0.45)}`,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55, display: "block" }}>
      {text}
    </Typography>
  </Box>
);

const GuiaReferenciaPanel = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const p = (key) => t(`crearMensajes.referenceGuide.${key}`);
  const primary = theme.palette.primary.main;

  const sins = ["pereza", "soberbia", "envidia", "avaricia"];
  const sentidos = ["vista", "tacto", "oido", "olfato", "gusto", "ser"];
  const storytellingKeys = ["personajes", "argumento", "trama", "drama"];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.8),
        overflow: "hidden",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(160deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 48%)`
            : `linear-gradient(160deg, ${alpha("#f3e8ff", 0.55)} 0%, ${alpha(theme.palette.background.paper, 0.99)} 52%, ${alpha("#eef4ff", 0.4)} 100%)`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 8px 28px rgba(0,0,0,0.22)"
            : "0 10px 36px rgba(15,23,42,0.06)",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderBottom: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.6),
          bgcolor: alpha(theme.palette.background.paper, 0.55),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(primary, 0.12),
              color: primary,
            }}
          >
            <MenuBookOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {p("title")}
            </Typography>
            <Chip
              size="small"
              label="Intereses · emoción · storytelling"
              sx={{
                mt: 0.4,
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 700,
                bgcolor: alpha(primary, 0.08),
              }}
            />
          </Box>
        </Stack>
      </Box>

      <Box sx={{ px: 1.25, pt: 1.25 }}>
        <TintCard color={primary} sx={{ py: 1.25 }}>
          <MensajeVennDiagram />
        </TintCard>

        <Divider sx={{ my: 1.5, borderColor: alpha(theme.palette.divider, 0.5) }} />

        <TintCard color={theme.palette.info.main} sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {p("intro")}
          </Typography>
        </TintCard>

        <SectionHeader
          title={p("sinsTitle")}
          accent={theme.palette.warning.dark}
          icon={LocalFireDepartmentOutlinedIcon}
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0.85,
            mb: 1.75,
          }}
        >
          {sins.map((key) => {
            const meta = SIN_META[key];
            const color = theme.palette[meta.palette].main;
            return (
              <SinCard
                key={key}
                name={p(`sins.${key}.name`)}
                example={p(`sins.${key}.example`)}
                color={color}
                icon={meta.Icon}
              />
            );
          })}
        </Box>

        <Box
          sx={{
            mb: 1,
            p: 1,
            borderRadius: 2,
            border: "1px solid",
            borderColor: alpha(theme.palette.error.main, 0.28),
            bgcolor: alpha(theme.palette.error.main, 0.06),
          }}
        >
          <SectionHeader
            title={p("emocionTitle")}
            accent={theme.palette.error.main}
            icon={FavoriteBorderOutlinedIcon}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: "block", mb: 1 }}>
          {p("problemaDolorIntro")}
        </Typography>

        <Stack spacing={0.85} sx={{ mb: 1.75 }}>
          <TintCard color={theme.palette.info.main}>
            <Typography variant="caption" sx={{ lineHeight: 1.55, display: "block" }}>
              <Box component="span" sx={{ fontWeight: 800, color: theme.palette.info.dark }}>
                {p("problemaLabel")}
              </Box>{" "}
              <Box component="em" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                {p("problemaEjemplo")}
              </Box>
            </Typography>
          </TintCard>
          <TintCard color={theme.palette.error.main}>
            <Typography variant="caption" sx={{ lineHeight: 1.55, display: "block" }}>
              <Box component="span" sx={{ fontWeight: 800, color: theme.palette.error.dark }}>
                {p("dolorLabel")}
              </Box>{" "}
              <Box component="em" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                {p("dolorEjemplo")}
              </Box>
            </Typography>
          </TintCard>
        </Stack>

        <SectionHeader
          title={p("sentidosTitle")}
          accent={primary}
          icon={VisibilityOutlinedIcon}
        />
        <Stack spacing={0.75} sx={{ mb: 1 }}>
          {sentidos.map((key) => {
            const meta = SENTIDO_META[key];
            const color = theme.palette[meta.palette].main;
            return (
              <SentidoCard
                key={key}
                label={p(`sentidos.${key}.label`)}
                quote={p(`sentidos.${key}.quote`)}
                color={color}
                icon={meta.Icon}
              />
            );
          })}
        </Stack>
        <TintCard color={primary} sx={{ mb: 1.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {p("sentidosFooter")}
          </Typography>
        </TintCard>

        <SectionHeader
          title={p("storytellingTitle")}
          accent={theme.palette.secondary.main}
          icon={AutoStoriesOutlinedIcon}
        />
        <TintCard color={theme.palette.secondary.main}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: "block", mb: 1.1 }}>
            {p("storytellingIntro")}
          </Typography>
          <Stack spacing={1}>
            {storytellingKeys.map((key) => (
              <StoryStep
                key={key}
                text={p(`storytelling.${key}`)}
                color={theme.palette.secondary.main}
              />
            ))}
          </Stack>
        </TintCard>
      </Box>
    </Paper>
  );
};

export default GuiaReferenciaPanel;
